/**
 * growth_packs_first_v1 A/B test (started 2026-10-28): in the countries where
 * plans rarely get paid, does opening the upgrade modal on credit packs
 * instead of plans turn more paywall visitors into buyers?
 *
 * Oct 28 2026 read (Stripe Checkout Sessions Sep 27 – Oct 28, country = the
 * Adaptive Pricing currency): in the 19 currencies below, plan checkouts
 * closed 622 → 15 paid (2.4%) and pack checkouts 173 → 21 (12.1%); in every
 * other currency plans 19% and packs 52%. Indonesia: 111 checkouts, none
 * paid. Stripe offers only cards and Link in these countries, and a
 * cross-border subscription takes cards only. Meanwhile the modal opens on
 * the yearly plans for every account that never paid.
 *
 * "control" opens the modal on the tab the caller asked for, as today.
 * "packs" opens it on the top-up tab; the plan tabs stay one tap away.
 *
 * Who is in the test: a visitor whose device time zone is in one of the 19
 * countries, when the modal would open on a plan tab and the account has no
 * active paid plan. Never drawn: the tool pages' pop-up (creative_tool_*
 * triggers belong to tool_lite_offer_v1) and the resume-checkout banner (the
 * visitor already picked a plan there).
 *
 * The country comes from the time zone, not the IP: solmi.ai is served
 * straight from Railway's edge, with no geo header, and a phone's time zone
 * names its country in these markets.
 *
 * The browser is the unit: drawn on the first eligible opening and kept in
 * localStorage. A signed-in account draws from a salted FNV-1a bucket of its
 * user id, so it gets the same arm on every device it signs in on.
 *
 * Kill switch: VITE_GROWTH_PACKS_FIRST_PERCENT=0 (build-time, default 50)
 * opens every modal as the caller asked, draws nothing and stamps nothing.
 * Other values only decide new draws; stored arms stay.
 *
 * Readout: `growth_packs_first_exposure` (experiment, arm, country, trigger,
 * requested_tab) on every eligible opening in either arm, the same arm on
 * upgrade_modal_shown, and the person property growth_packs_first_v1_arm (set
 * once on the exposure, and again at sign-up/in through useAuth, since
 * PostHog drops $set_once for anonymous visitors). Then `purchase` and
 * `credit_topup_purchased` per exposed person by arm: buyers and revenue.
 * Split by device: shortfall_ask_v1 changed this modal's opening and cut
 * mobile buyers about 3x, so mobile buyers are the guardrail.
 */

import { fnv1a32 } from "./directorStyleBoost";

export const GROWTH_PACKS_FIRST_EXPERIMENT = "growth_packs_first_v1";

export type GrowthPacksFirstArm = "control" | "packs";

/** The modal tabs (UpgradeModal's UpgradeModalTab). */
type ModalTab = "monthly" | "yearly" | "topup";

export const GROWTH_PACKS_FIRST_STORAGE_KEY = `solmi:exp:${GROWTH_PACKS_FIRST_EXPERIMENT}`;

/** The person property carrying the arm. */
export const GROWTH_PACKS_FIRST_PERSON_PROPERTY = `${GROWTH_PACKS_FIRST_EXPERIMENT}_arm`;

/**
 * The 19 markets of the Oct 28 read, by currency: INR IDR BRL PHP ZAR BDT VND
 * MXN MYR PKR NGN EGP LKR MAD TRY COP PEN KES THB. Each lists its IANA time
 * zones, with the old names browsers still report (Asia/Calcutta,
 * Asia/Saigon …).
 */
export const GROWTH_COUNTRY_TIME_ZONES = {
  IN: ["Asia/Kolkata", "Asia/Calcutta"],
  ID: [
    "Asia/Jakarta",
    "Asia/Pontianak",
    "Asia/Makassar",
    "Asia/Ujung_Pandang",
    "Asia/Jayapura",
  ],
  BR: [
    "America/Sao_Paulo",
    "America/Bahia",
    "America/Fortaleza",
    "America/Recife",
    "America/Maceio",
    "America/Belem",
    "America/Araguaina",
    "America/Santarem",
    "America/Manaus",
    "America/Boa_Vista",
    "America/Porto_Velho",
    "America/Cuiaba",
    "America/Campo_Grande",
    "America/Rio_Branco",
    "America/Porto_Acre",
    "America/Eirunepe",
    "America/Noronha",
    "Brazil/East",
    "Brazil/West",
    "Brazil/Acre",
    "Brazil/DeNoronha",
  ],
  PH: ["Asia/Manila"],
  ZA: ["Africa/Johannesburg"],
  BD: ["Asia/Dhaka", "Asia/Dacca"],
  VN: ["Asia/Ho_Chi_Minh", "Asia/Saigon"],
  MX: [
    "America/Mexico_City",
    "America/Cancun",
    "America/Merida",
    "America/Monterrey",
    "America/Matamoros",
    "America/Chihuahua",
    "America/Ciudad_Juarez",
    "America/Ojinaga",
    "America/Mazatlan",
    "America/Bahia_Banderas",
    "America/Hermosillo",
    "America/Tijuana",
    "America/Ensenada",
    "America/Santa_Isabel",
    "Mexico/General",
    "Mexico/BajaNorte",
    "Mexico/BajaSur",
  ],
  MY: ["Asia/Kuala_Lumpur", "Asia/Kuching"],
  PK: ["Asia/Karachi"],
  NG: ["Africa/Lagos"],
  EG: ["Africa/Cairo", "Egypt"],
  LK: ["Asia/Colombo"],
  MA: ["Africa/Casablanca"],
  TR: ["Europe/Istanbul", "Asia/Istanbul", "Turkey"],
  CO: ["America/Bogota"],
  PE: ["America/Lima"],
  KE: ["Africa/Nairobi"],
  TH: ["Asia/Bangkok"],
} as const satisfies Record<string, readonly string[]>;

export type GrowthCountry = keyof typeof GROWTH_COUNTRY_TIME_ZONES;

const COUNTRY_BY_TIME_ZONE = new Map<string, GrowthCountry>(
  (Object.keys(GROWTH_COUNTRY_TIME_ZONES) as GrowthCountry[]).flatMap(
    (country) =>
      GROWTH_COUNTRY_TIME_ZONES[country].map(
        (zone) => [zone, country] as const,
      ),
  ),
);

function deviceTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * The growth market a time zone belongs to, or null. `timeZone` defaults to
 * the device's.
 */
export function growthCountryFromTimeZone(
  timeZone: string | undefined = deviceTimeZone(),
): GrowthCountry | null {
  return (timeZone && COUNTRY_BY_TIME_ZONE.get(timeZone)) || null;
}

export function growthPacksFirstPercent(raw: unknown): number {
  if (raw === undefined || raw === null || raw === "") return 50;
  const value = Number(raw);
  if (!Number.isFinite(value)) return 50;
  return Math.min(100, Math.max(0, Math.round(value)));
}

export function growthPacksFirstBucket(seed: string): number {
  return fnv1a32(`${GROWTH_PACKS_FIRST_EXPERIMENT}:${seed}`) % 100;
}

/** Triggers that never draw: their pop-up or plan belongs elsewhere. */
export function growthPacksFirstExcludedTrigger(trigger: string): boolean {
  return (
    trigger.startsWith("creative_tool_") ||
    trigger === "resume_checkout_banner"
  );
}

/**
 * Whether this opening may draw (or use) an arm: a growth-market visitor, a
 * modal about to open on a plan tab, no active paid plan, and a trigger that
 * isn't excluded.
 */
export function growthPacksFirstEligible(input: {
  country: GrowthCountry | null;
  requestedTab: ModalTab;
  trigger: string;
  hasActivePaidPlan: boolean;
}): boolean {
  return (
    input.country !== null &&
    input.requestedTab !== "topup" &&
    !input.hasActivePaidPlan &&
    !growthPacksFirstExcludedTrigger(input.trigger)
  );
}

function isArm(value: unknown): value is GrowthPacksFirstArm {
  return value === "control" || value === "packs";
}

type ArmStorage = Pick<Storage, "getItem" | "setItem">;

function browserStorage(): ArmStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    // Reading window.localStorage itself throws when site data is blocked.
    return null;
  }
}

function readStoredArm(storage: ArmStorage | null): GrowthPacksFirstArm | null {
  try {
    const stored = storage?.getItem(GROWTH_PACKS_FIRST_STORAGE_KEY);
    return isArm(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * This browser's arm, or null when the test is off. Reads the stored arm
 * first; otherwise draws one (from the user id when given, else at random)
 * and stores it. Storage that throws still yields an arm, just not a sticky
 * one. `storage` defaults to localStorage.
 */
export function growthPacksFirstArm(input: {
  userId?: string | number | null;
  percent: number;
  storage?: ArmStorage | null;
  random?: () => number;
}): GrowthPacksFirstArm | null {
  if (input.percent <= 0) return null;
  const storage =
    input.storage === undefined ? browserStorage() : input.storage;
  const stored = readStoredArm(storage);
  if (stored) return stored;
  const userId =
    input.userId === null || input.userId === undefined
      ? ""
      : String(input.userId);
  const bucket = userId
    ? growthPacksFirstBucket(userId)
    : Math.floor((input.random ?? Math.random)() * 100);
  const arm: GrowthPacksFirstArm = bucket < input.percent ? "packs" : "control";
  try {
    storage?.setItem(GROWTH_PACKS_FIRST_STORAGE_KEY, arm);
  } catch {}
  return arm;
}

/** The tab the modal opens on: the top-up packs in the "packs" arm. */
export function growthPacksFirstTab(
  requestedTab: ModalTab,
  arm: GrowthPacksFirstArm | null,
): ModalTab {
  return arm === "packs" ? "topup" : requestedTab;
}

export interface GrowthPacksFirstDraw {
  arm: GrowthPacksFirstArm;
  country: GrowthCountry;
}

/** What growth_packs_first_exposure carries (plus trigger and tab). */
export function growthPacksFirstEventProps(
  draw: GrowthPacksFirstDraw | null,
): Record<string, string> {
  return draw
    ? {
        experiment: GROWTH_PACKS_FIRST_EXPERIMENT,
        arm: draw.arm,
        country: draw.country,
      }
    : {};
}

/**
 * The same draw on upgrade_modal_shown, under keys of its own: that event's
 * `experiment` belongs to shortfall_ask_v1.
 */
export function growthPacksFirstModalProps(
  draw: GrowthPacksFirstDraw | null,
): { growth_packs_first_arm?: GrowthPacksFirstArm; growth_country?: GrowthCountry } {
  return draw
    ? {
        growth_packs_first_arm: draw.arm,
        growth_country: draw.country,
      }
    : {};
}

/** The person property to set once, or undefined outside the test. */
export function growthPacksFirstPersonProps(
  arm: GrowthPacksFirstArm | null,
): Record<string, string> | undefined {
  return arm ? { [GROWTH_PACKS_FIRST_PERSON_PROPERTY]: arm } : undefined;
}

/**
 * The person property to set once when a visitor signs up or in (useAuth's
 * identify call): the arm this browser already holds, undefined if none or
 * with the test off. It never draws.
 */
export function growthPacksFirstIdentifyProps(
  rawPercent: unknown,
  storage?: ArmStorage | null,
): Record<string, string> | undefined {
  if (growthPacksFirstPercent(rawPercent) <= 0) return undefined;
  return growthPacksFirstPersonProps(
    readStoredArm(storage === undefined ? browserStorage() : storage),
  );
}
